import React, { useState } from 'react';
import { Box, IconButton, Tooltip, CircularProgress, Dialog } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CloseIcon from '@mui/icons-material/Close';
import { saveProductImages } from '../../databaseManager';

// Moves the image at `fromIdx` (an index into the *visible* subset) to `toIdx`,
// leaving hidden entries in their original relative positions.
function reorderVisible(mainImages, fromIdx, toIdx) {
  const visibleIndices = mainImages
    .map((img, i) => ({ img, i }))
    .filter(({ img }) => !img.isHidden)
    .map(({ i }) => i);

  const order = visibleIndices.map((i) => mainImages[i]);
  const [moved] = order.splice(fromIdx, 1);
  order.splice(toIdx, 0, moved);

  const next = [...mainImages];
  visibleIndices.forEach((origIdx, k) => { next[origIdx] = order[k]; });
  return next;
}

/**
 * Editable grid of a product's photos: drag to reorder, click to delete.
 * Deletion is a soft-hide (isHidden: true) to match how the iOS app treats
 * removed photos, rather than deleting the underlying image.
 *
 * Props:
 *  images     — raw mainImages array ({ urlString, isHidden, ... }), in display order
 *  customer   — customer id
 *  productUri — product doc id
 *  onSaved    — (newMainImages) => void, called after a successful Firestore write
 */
export default function ProductPhotoManager({ images, customer, productUri, onSaved }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const [zoomedUrl, setZoomedUrl] = useState(null);

  const visible = images.filter((img) => !img.isHidden);

  const persist = async (nextMainImages) => {
    setSaving(true);
    try {
      await saveProductImages(customer, productUri, nextMainImages);
      onSaved(nextMainImages);
    } catch (err) {
      console.error('Failed to save photo changes:', err);
      alert(`Failed to save photo changes: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDrop = (dropIdx) => {
    if (dragIndex === null || dragIndex === dropIdx) { setDragIndex(null); return; }
    const next = reorderVisible(images, dragIndex, dropIdx);
    setDragIndex(null);
    persist(next);
  };

  const handleDelete = (idx) => {
    const target = visible[idx];
    if (!window.confirm('Remove this photo?')) return;
    const next = images.map((img) => (img === target ? { ...img, isHidden: true } : img));
    persist(next);
  };

  if (visible.length === 0) return null;

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(90px, 1fr))',
          gap: 1,
          p: 1.25,
          opacity: saving ? 0.5 : 1,
          pointerEvents: saving ? 'none' : 'auto',
        }}
      >
        {visible.map((img, idx) => (
          <Box
            key={img.id || img.urlString || idx}
            draggable
            onDragStart={() => setDragIndex(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(idx)}
            onDragEnd={() => setDragIndex(null)}
            sx={{
              position: 'relative',
              width: '100%',
              aspectRatio: '1 / 1',
              cursor: 'grab',
              outline: dragIndex === idx ? '2px dashed' : 'none',
              outlineColor: 'primary.main',
              borderRadius: 1.5,
              overflow: 'hidden',
              '&:hover .photo-actions': { opacity: 1 },
            }}
          >
            <Box
              component="img"
              src={img.urlString}
              alt=""
              draggable={false}
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <Box
              className="photo-actions"
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                p: 0.25,
                opacity: 0,
                transition: 'opacity 0.15s',
                background: 'linear-gradient(180deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0) 40%)',
              }}
            >
              <DragIndicatorIcon fontSize="small" sx={{ color: 'common.white' }} />
              <Tooltip title="Remove photo">
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleDelete(idx); }}
                  sx={{ color: 'common.white', p: 0.25 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
            <Tooltip title="Enlarge photo">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); setZoomedUrl(img.urlString); }}
                sx={{
                  position: 'absolute',
                  bottom: 4,
                  right: 4,
                  color: 'common.white',
                  bgcolor: 'rgba(0,0,0,0.45)',
                  p: 0.4,
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
                }}
              >
                <ZoomInIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Box>
      {saving && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CircularProgress size={16} />
        </Box>
      )}

      <Dialog open={!!zoomedUrl} onClose={() => setZoomedUrl(null)} maxWidth="lg">
        <Box sx={{ position: 'relative', lineHeight: 0 }}>
          <IconButton
            onClick={() => setZoomedUrl(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'common.white',
              bgcolor: 'rgba(0,0,0,0.45)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {zoomedUrl && (
            <Box
              component="img"
              src={zoomedUrl}
              alt=""
              sx={{ display: 'block', maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </Box>
      </Dialog>
    </Box>
  );
}
